import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
	ChampionshipComparison,
	closeLeaderboardPopup,
	flashError,
	getChampionshipOptInContext,
	getChampionshipSuggestions,
	getLeaderboard,
	getMyLeaderboard,
	joinFreeChampionship,
	joinPaidChampionship,
	loadLeaderboardFirstPage,
	openPlayerPopup,
	reloadMyChampionships,
	removeFromChampionshipWatchList,
	showAddToChampionshipWatchListModal,
	showChallengeLeaderboardModal,
	showComparisonLeaderboardModal,
	showInviteModal,
} from "../../actions";
import {
	AdLocation,
	Card,
	Championship,
	ChampionshipLeague,
	ChampionshipOptInContext,
	LeaderboardData,
	LeaderboardQueryParams,
	Profile,
	Rank,
	User,
} from "../../types";
import { InvisibleInfiniteLoader } from "../infiniteLoader";
import PlayerItemMedium from "../items/player/medium";
import DefaultPlayerMeta from "../items/player/defaultMeta";
import cx from "classnames";
import { _ } from "libs";
import { formatUserName } from "../../libs/formatName";
import ChampionshipAbout from "./championshipAbout";
import ChampionshipTrial from "./championshipTrial";
import ChampionshipMyRanking from "./championshipMyRanking";
import UserContext from "../../contexts/userContext";
import withRedux from "../../withRedux";
import { RootState } from "../../reducers";
import championshipSelector from "../../selectors/championshipSelector";
import InView from "react-intersection-observer";
import { animatedWindowScroll } from "../../actions/animatedWindowScroll";
import ScrollWatcher from "scroll-watcher";
import Challenges from "./challenges";
import Sponsors from "../sponsors";
import PlayerFilter, { defaultFilter, PlayersFilter, PlayersFilterType } from "../playerFilter";
import OpponentSuggestions from "./opponentSuggestions";
import { getLeagueInfo } from "../items/player/playerHelper";
import AdWidget from "../adWidget";
import { hydrateFilter, persistFilter } from "../../libs/filterStorage";
import UpcomingReservations from "../upcomingReservations";
import Comparisons from "./comparisons";
import PaymentModal from "../paymentModal";
import ChampionshipContext from "../../contexts/championshipContext";
import LinkedTournaments from "./linkedTournaments";
import { useStripe } from "@stripe/react-stripe-js";

type RowProps = {
	player: User;
	rank: Rank;
	position: number;
	championship?: any;
	onClick: (player: User) => void;
	selected: boolean;
	index: number;
	league?: ChampionshipLeague;
	reference?: any;
	onRemove?: () => void;
};

export const Row = ({
	player,
	rank,
	position,
	championship,
	onClick,
	selected = false,
	index,
	league,
	reference,
	onRemove,
}: RowProps) => {
	const { profile } = useContext(UserContext);
	const isMyself = player.userId === profile.userId;
	const playerName = formatUserName(player);
	const playerNameWithPosition = `${position ? position + ". " : ""}${playerName}`;
	const showQualifiers =
		league &&
		((championship?.league?.players < league.qualifiers && index + 1 === championship?.league?.players) ||
			index === league.qualifiers - 1);
	const cellBgColor = league && !isMyself && index < league.qualifiers ? league?.colors?.background : null;
	const isTournament = championship?.championshipType === "tournament";

	return (
		<>
			<div
				id={isMyself ? "mine" : rank.rank === 1 ? "first" : null}
				onClick={() => onClick(player)}
				className={cx("cell", { selected, own: isMyself })}
				style={{ backgroundColor: cellBgColor }}
				ref={reference}
				key={"row-" + player.userId}
			>
				<PlayerItemMedium
					photoUrl={player.photoUrl}
					name={isTournament ? playerName : playerNameWithPosition}
					meta={DefaultPlayerMeta(player)}
					points={rank.points || 0}
					score={rank.score}
					isBuddy={!isMyself && player.userRelationship === "accepted"}
					leagueId={player.leagueId}
					championship={championship}
					// officialRating={player.rating.officialRating} TODO @JURAJ will need to do this differently
					// meta={_("leaderboard.player.rank", null, { rank: rank.rank, points: rank.points || 0 })}
				/>
				{onRemove && !isMyself && (
					<div className="remove">
						<div
							className="icon cancel"
							onClick={(e) => {
								e.stopPropagation();
								onRemove();
							}}
						/>
					</div>
				)}
			</div>
			{showQualifiers && (
				<div className="qualifiersBar">
					<div className="icon arrow" />
					<div className="title">{_("championship.leaderboard.qualificationZone")}</div>
					<div className="icon arrow" />
				</div>
			)}
		</>
	);
};

const PAGE_SIZE = 20;
const SEARCH_UP = "up";
const SEARCH_DOWN = "down";

type Props = {
	uuid: string;
	firstPage: LeaderboardData[];
	loadLeaderboardFirstPage;
	showComparisonLeaderboardModal;
	showChallengeLeaderboardModal;
	showInviteModal;
	getChampionshipSuggestions;
	reloadMyChampionships;
	showAddToChampionshipWatchListModal;
	championship: Championship;
	profile: Profile;
};

const useLeaderboard = (
	initalState: LeaderboardData[] = []
): [LeaderboardData[], (data: LeaderboardData[]) => void] => {
	const [leaderboards, setLeaderboard] = useState(initalState);

	const updateLeaderboard = (data: LeaderboardData[]) => {
		const unique = [] as LeaderboardData[];

		const ids = [] as string[];

		for (let item of data) {
			if (ids.indexOf(item.player.userId) === -1) {
				unique.push(item);
				ids.push(item.player.userId);
			}
		}

		//[...new Set(data)]
		const sorted = unique.sort((a, b) => {
			const rankA = a.rank.rank;
			const rankB = b.rank.rank;

			if (rankA === null && rankB === null) {
				return 0;
			}

			if (rankA !== null && rankB === null) {
				return -1;
			}

			if (rankA === null && rankB !== null) {
				return 1;
			}

			if (rankA < rankB) {
				return -1;
			}

			return 1;
		});

		setLeaderboard(sorted);
	};

	return [leaderboards, updateLeaderboard];
};

const Leaderboard = ({
	uuid,
	firstPage,
	loadLeaderboardFirstPage,
	showComparisonLeaderboardModal,
	showInviteModal,
	reloadMyChampionships,
	getChampionshipSuggestions,
	showChallengeLeaderboardModal,
	showAddToChampionshipWatchListModal,
	championship,
	profile,
}: Props) => {
	const [leaderboards, updateLeaderboard] = useLeaderboard([]);
	const [filter, setFilter] = useState({} as LeaderboardQueryParams);
	const [comparisons, setComparisons] = useState([] as ChampionshipComparison[]);
	const [firstLeaderboards, updateFirstLeaderboard] = useLeaderboard([]);
	const [selectedUser, selectUser] = useState(null as User);
	const [loading, setLoading] = useState(true);
	const [showFirstPage, setShowFirstPage] = useState(false);
	const [pagesLoaded, updatePagesLoaded] = useState([0] as number[]);
	const [topPagesLoaded, updateTopPagesLoaded] = useState([0] as number[]);
	const [selectedLeague, setSelectedLeague] = useState(null as ChampionshipLeague);
	const [myRank, setMyRank] = useState(null as Rank);
	const [hasMore, setHasMore] = useState(true);
	const [wasScrolled, setWasScrolled] = useState(false);
	const [firstInView, setFirstInView] = useState(false);
	const [meInView, setMeInView] = useState(false);
	const stripe = useStripe();
	const scrollDelta = useRef(0);

	const FILTER_NAME = championship?.uuid + "_player-filter";

	const modal = useRef<typeof PaymentModal>(null as typeof PaymentModal);

	useEffect(() => {
		let unmounted = false;

		if (!unmounted) {
			if (firstPage.length > 0) {
				updateFirstLeaderboard(firstPage);
				updateLeaderboard(firstPage);
			}
		}

		return () => {
			unmounted = true;
		};
	}, [firstPage]);

	useEffect(() => {
		if (firstPage.length === 0) {
			//loadLeaderboardFirstPage(uuid);
		}
		setLoading(false);
	}, [uuid]);

	useEffect(() => {
		if (championship) {
			console.log(championship);
			loadLeaderboardFirstPage(uuid);
			setShowFirstPage(true);
			setHasMore(true);
			setMyRank(championship.rank);
			/**
			if (championship.optedIn && championship.matchesPlayed > 0) {
				searchMyPosition();
				setMyRank(championship.rank);
			} else {
				loadLeaderboardFirstPage(uuid);
				setShowFirstPage(true);
				setHasMore(true);
			}
			 **/
		}
	}, [championship]);

	useEffect(() => {
		if (Object.keys(filter).length === 0) {
			return;
		}
		setLoading(true);
		updateFirstLeaderboard([]);
		updateLeaderboard([]);
		updatePagesLoaded([0]);
		setShowFirstPage(true);
		setHasMore(true);
		getLeaderboard(uuid, 0, PAGE_SIZE, filter).then(
			(res) => {
				if (!res) {
					setLoading(false);
					return;
				}
				const { data, pageRows, pageCurrent } = res;
				updatePagesLoaded([0]);
				updateFirstLeaderboard(data);
				updateLeaderboard(data);
				setShowFirstPage(true);
				setHasMore(true);
				setLoading(false);
			},
			(err) => {
				setLoading(false);
			}
		);
	}, [filter]);

	useEffect(() => {
		window.addEventListener("scroll", scrollHandler);
		window.addEventListener("wheel", scrollHandler);

		const watcher = new ScrollWatcher();
		watcher.on("scrolling", function (evt) {
			if (evt.scrollingDown) {
				scrollDelta.current = 3;
			}

			if (evt.scrollingUp) {
				scrollDelta.current = -3;
			}
		});

		return () => {
			window.removeEventListener("scroll", scrollHandler);
			window.addEventListener("wheel", scrollHandler);
			watcher.off("scrolling");
		};
	}, []);

	const onJoinClick = () => {
		if (championship.settings.optInFee === 0) {
			joinFreeChampionship(championship.uuid).then((res) => {
				reloadMyChampionships();
			});
		} else if (championship.settings.trial.canStartTrial === true && !championship.settings.trial.cardForTrial) {
			if (
				window.confirm(
					_("championship.confirm_start_trial.prompt", null, { championshipName: championship.name })
				)
			) {
				joinFreeChampionship(championship.uuid, true).then((res) => {
					reloadMyChampionships();
				});
			}
		} else {
			modal.current.open();
		}
	};

	const onPayClicked = async (
		card: Card,
		voucherCode?: string,
		promotionId?: string,
		network?: string,
		paymentMethod?: string,
		startTrial?: boolean
	): Promise<void> => {
		return await joinPaidChampionship(
			stripe,
			championship.uuid,
			card.cardId,
			voucherCode,
			promotionId,
			paymentMethod,
			network,
			null,
			startTrial
		).then(
			async (res) => {
				modal?.current?.close();
				await reloadMyChampionships();
			},
			(err: Error) => {
				flashError(err.message);
			}
		);
	};

	const toggleLeagueFilter = (showLeague: boolean) => {
		if (showLeague) {
			setFilter({ ...filter, leagueId: championship?.league?.id });
			setSelectedLeague(getLeagueInfo(championship, championship?.league?.id));
		} else {
			setFilter({ ...filter, leagueId: null });
			setSelectedLeague(null);
		}
	};

	const updateFilter = (newFilter: LeaderboardQueryParams) => {
		updateFilterNewValues({
			...newFilter,
			leagueId: filter.leagueId,
		});
	};

	const hasActiveOrExpiredTrial = (): boolean => {
		return championship?.settings.trial?.hasActiveTrial || championship?.settings.trial?.hasExpiredTrial;
	};

	const hasExpiredTrial = (): boolean => {
		return championship?.settings.trial?.hasExpiredTrial;
	};

	const updateFilterNewValues = (newFilter: LeaderboardQueryParams) => {
		setFilter(newFilter);
	};

	const resetFilter = () => {
		const newFilter = {
			leagueId: filter.leagueId,
		};
		persistFilter(FILTER_NAME, newFilter, profile.userId);
		updateFilter(newFilter);
	};

	const onChallengeSelected = (challengeId, title) => {
		if (!challengeId) {
			return;
		}

		showChallengeLeaderboardModal(challengeId, championship, null, title);
	};

	const onComparisonSelected = (params, title) => {
		if (!params) {
			return;
		}

		let filterParams: LeaderboardQueryParams = {};
		let addFunction, removeFunction;

		if (params.longitude && params.latitude) {
			filterParams = {
				longitude: params.longitude,
				latitude: params.latitude,
			};
		} else if (params.clubId) {
			filterParams = {
				clubId: params.clubId,
			};
		} else if (params.watchlist) {
			filterParams = {
				watchlist: true,
			};
			addFunction = () => {
				closeLeaderboardPopup();
				showAddToChampionshipWatchListModal(championship, () => {
					onComparisonSelected(params, title);
				});
			};
			removeFunction = (playerId) => {
				return removeFromChampionshipWatchList(championship.uuid, playerId);
			};
		}

		showComparisonLeaderboardModal(
			filterParams,
			false,
			championship,
			null,
			title,
			null,
			addFunction,
			removeFunction
		);
	};

	const onPlayerFilterChange = (filter: PlayersFilter) => {
		persistFilter(FILTER_NAME, filter, profile.userId);
		updateFilter(mergeFilterWithParams(filter));
	};

	function mergeFilterWithParams(filter: PlayersFilter) {
		let [ageFrom, ageTo] = filter.age || [null, null];
		let age = { ageFrom, ageTo } as any;
		const [defaultAgeFrom, defaultAgeTo] = defaultFilter.age;

		if (ageFrom === defaultAgeFrom && ageTo === defaultAgeTo) {
			age = {};
		}
		const gender = filter.gender === "any" ? null : filter.gender;
		const { lng: longitude, lat: latitude } = filter.location?.location
			? filter.location.location
			: { lng: null, lat: null };
		return { ...age, gender, longitude, latitude };
	}

	const scrollHandler = (e) => {
		setWasScrolled(true);
		window.removeEventListener("scroll", scrollHandler);
		window.addEventListener("wheel", scrollHandler);
	};

	const searchMyPosition = async () => {
		setLoading(true);
		const { data } = await getMyLeaderboard(uuid);
		updateLeaderboard(data);
		setLoading(false);
	};

	const search = async (page: number, direction: string) => {
		if (pagesLoaded.indexOf(page) === -1) {
			setLoading(true);
			const { data, pageRows, pageCurrent } = await getLeaderboard(uuid, page, PAGE_SIZE, filter);

			if (data) {
				let newUsers = [];

				switch (direction) {
					case SEARCH_UP: {
						newUsers = [...data, ...leaderboards];
						break;
					}

					case SEARCH_DOWN: {
						newUsers = [...leaderboards, ...data];
						if (data.length < PAGE_SIZE) {
							setHasMore(false);
						}
						break;
					}
				}

				updateLeaderboard(newUsers);
				updatePagesLoaded(pagesLoaded.concat(page));
			}
			setLoading(false);
		}
	};

	const searchFromTop = async (page: number) => {
		if (topPagesLoaded.indexOf(page) === -1) {
			const { data, pageRows, pageCurrent } = await getLeaderboard(uuid, page, PAGE_SIZE, filter);

			if (data) {
				const newUsers = [...firstLeaderboards, ...data];
				updateFirstLeaderboard(newUsers);
				updateTopPagesLoaded(topPagesLoaded.concat(page));
			}
		}
	};

	const scrollToFirst = () => {
		if (leaderboards.length > 0 && leaderboards[0].rank.rank > 1 && showFirstPage === false) {
			setShowFirstPage(true);
		}
		animatedWindowScroll("y", 0, 300);
		setFirstInView(true);
	};

	const scrollToMe = () => {
		const me = document.getElementById("mine");
		animatedWindowScroll("y", me.offsetTop - 80, 300);
		setMeInView(true);
	};

	const getCurrentPageUp = (): number | null => {
		let highestRank = null as number;

		for (let i = 0; i <= leaderboards.length - 1; i++) {
			const player = leaderboards[i];

			if (player.rank.rank) {
				highestRank = player.rank.rank;
				break;
			}
		}

		if (highestRank === null) {
			return null;
		}

		return Math.max(0, Math.ceil(highestRank / PAGE_SIZE) - 1);
	};

	const getCurrentPageDown = (): number | null => {
		let lowestRank = null as number;

		const reversed = [...leaderboards].reverse();

		for (let i = 0; i <= leaderboards.length - 1; i++) {
			const player = reversed[i];

			if (player.rank.rank) {
				lowestRank = player.rank.rank;
				break;
			}
		}

		if (lowestRank === null) {
			return null;
		}
		return Math.ceil(lowestRank / PAGE_SIZE) - 1;
	};

	const pageAlreadyLoaded = (page: number): boolean => {
		return pagesLoaded.indexOf(page) !== -1;
	};

	const getChampionshipContext = async (
		voucherCode?: string,
		promotionId?: string,
		startTrial?: boolean
	): Promise<ChampionshipOptInContext> => {
		return getChampionshipOptInContext(championship.uuid, voucherCode, promotionId, startTrial);
	};

	const onViewChangeUp = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		const shouldLoad = inView && leaderboards.length > 0 && wasScrolled && scrollDelta.current < 0;

		if (shouldLoad) {
			const currentPage = getCurrentPageUp();
			if (currentPage !== null) {
				const page = Math.max(0, currentPage - 1);

				if (page === 0) {
					//updateLeaderboard([...firstPage, ...leaderboards]);
					setShowFirstPage(false);
				} else {
					search(page, SEARCH_UP);
				}
			}
		}
	};

	const onViewChangeDown = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		if (!championship) {
			return;
		}
		const shouldLoad = inView && (leaderboards.length > 0 || championship.optedIn === false) && hasMore;

		if (shouldLoad) {
			const currentPage = getCurrentPageDown();
			if (currentPage !== null) {
				const page = Math.max(0, currentPage + 1);

				if (!pageAlreadyLoaded(page)) {
					search(page, SEARCH_DOWN);
				} else {
					const highestPageLoaded = Math.max(...pagesLoaded);
					search(highestPageLoaded + 1, SEARCH_DOWN);
				}
			} else {
				const highestPageLoaded = Math.max(...pagesLoaded);
				search(highestPageLoaded + 1, SEARCH_DOWN);
			}
		}
	};

	const onViewChangeMiddle = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		const shouldLoad = inView && scrollDelta.current > 0; //&& leaderboards.length > 0;

		if (shouldLoad) {
			const page = Math.max(0, ...topPagesLoaded) + 1;
			searchFromTop(page);
		}
	};

	const onFirstViewChange = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		setFirstInView(inView);
	};

	const onMeViewChange = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		setMeInView(inView);
	};

	const FilterBlock = () => {
		return (
			<div className="blockFilter">
				<PlayerFilter
					onFilterChange={onPlayerFilterChange}
					value={hydrateFilter(FILTER_NAME, defaultFilter, profile.userId)}
					types={[PlayersFilterType.AGE, PlayersFilterType.GENDER, PlayersFilterType.LOCATION]}
				/>
				{Object.keys(filter).length > 2 && (
					<div className="button xsmall grey" onClick={resetFilter}>
						Reset
					</div>
				)}
			</div>
		);
	};

	const renderRow = ({ player, rank }: { player: User; rank: Rank }, key: number) => {
		if (rank.rank === 1) {
			return (
				<InView onChange={onFirstViewChange} key={`${player.userId}_${key}_${rank.rank}`}>
					{({ inView, ref }) => {
						return (
							<Row
								reference={ref}
								player={player}
								rank={rank}
								position={rank.rank}
								championship={championship}
								league={selectedLeague}
								index={key}
								onClick={() =>
									openPlayerPopup(
										player.userId,
										true,
										{ championshipName: championship.name },
										{
											championship,
											leagueId: player.leagueId,
										}
									)
								}
								selected={selectedUser ? player.userId === selectedUser.userId : false}
							/>
						);
					}}
				</InView>
			);
		}

		if (player.userId === profile.userId) {
			return (
				<InView onChange={onMeViewChange} key={`${player.userId}_${key}_${rank.rank}`}>
					{({ inView, ref }) => {
						return (
							<Row
								reference={ref}
								player={player}
								rank={rank}
								position={rank.rank}
								championship={championship}
								league={selectedLeague}
								index={key}
								onClick={() =>
									openPlayerPopup(
										player.userId,
										true,
										{ championshipName: championship.name },
										{ championship, leagueId: player.leagueId }
									)
								}
								selected={selectedUser ? player.userId === selectedUser.userId : false}
							/>
						);
					}}
				</InView>
			);
		}

		return (
			<Row
				player={player}
				rank={rank}
				position={rank.rank}
				championship={championship}
				index={key}
				league={selectedLeague}
				onClick={() =>
					openPlayerPopup(
						player.userId,
						true,
						{ championshipName: championship.name },
						{ championship, leagueId: player.leagueId }
					)
				}
				selected={selectedUser ? player.userId === selectedUser.userId : false}
				key={`${player.userId}_${key}_${rank.rank}`}
			/>
		);
	};

	let showComparisons = true;

	let isTournament = false;

	if (championship) {
		isTournament = championship.championshipType === "tournament";
	}

	const championshipContext = {
		onJoinClick,
		onPayClicked,
		modal,
		championship,
		context: getChampionshipContext,
	};

	return (
		<ChampionshipContext.Provider value={championshipContext}>
			<div className="wrap">
				<div className="sponsorsWrapper">
					<Sponsors resourceId={championship?.uuid} />
				</div>
				{hasExpiredTrial() && (
					<div className="trialExpiredBar">
						<div>{_("championship.trial.expired")}</div>
						<button className="button secondaryButton small" onClick={championshipContext.onJoinClick}>
							{_("championship.trial.purchaseBtn")}
						</button>
						{championship.settings.optInFee > 0 ? (
							<PaymentModal
								ref={modal}
								description={championship.name}
								amount={championship.settings.optInFee as number}
								currency={championship.settings.currency}
								onPayClicked={onPayClicked}
								context={getChampionshipContext}
							/>
						) : null}
					</div>
				)}
				<div className="contentWrapper">
					<div className="column columnLeft">
						{championship && championship.optedIn === false && isTournament === false && (
							<ChampionshipAbout />
						)}
						{championship && hasActiveOrExpiredTrial() && <ChampionshipTrial />}
						{championship && championship.optedIn === true && isTournament === false && (
							<>
								<ChampionshipMyRanking ranking={myRank} profile={profile} />
								{championship && <LinkedTournaments />}
								{showComparisons && <Comparisons onComparisonSelected={onComparisonSelected} />}
								<OpponentSuggestions onInvitePressed={() => showInviteModal(championship)} />
							</>
						)}
						{isTournament && <ChampionshipAbout />}
					</div>
					{championship &&
					championship.settings.showReadMoreFirst &&
					(championship.settings.readMoreUrl || championship.settings.embeddedReadMoreUrl) &&
					championship.optedIn === false ? (
						<div className="column columnFrame">
							<div className="nBlock">
								<iframe
									title="Competition website"
									className="championshipWebsite"
									src={
										//@todo handle locale in the query from the backend
										championship.settings.embeddedReadMoreUrl +
											"?lang=" +
											(profile.language || "en") ||
										championship.settings.readMoreUrl + "?lang=" + (profile.language || "en")
									}
								/>
							</div>
						</div>
					) : (
						<>
							<div className="column columnCenter">
								<div className="nBlock rankings">
									{championship?.league?.id ? (
										<>
											<div className="blockHeader">
												<div className="blockMenu">
													<div
														onClick={() => toggleLeagueFilter(false)}
														className={cx("blockMenuItem", { active: !filter.leagueId })}
													>
														{_("championship.leaderboard.rankings")}
													</div>
													<div
														onClick={() => toggleLeagueFilter(true)}
														className={cx("blockMenuItem", { active: filter.leagueId })}
													>
														{_("championship.leaderboard.leagueRankings")}
													</div>
												</div>
											</div>
											{isTournament === false && <FilterBlock />}
										</>
									) : (
										<>
											<div className="blockHeader">
												<h5 className="heading">
													{isTournament
														? _("championship.leaderboard.entryList")
														: _("championship.leaderboard.rankings")}
												</h5>
											</div>
											{isTournament === false && <FilterBlock />}
										</>
									)}
									<div className="table players" id="scroller">
										{!firstInView && leaderboards.length > 15 && (
											<div className="scrollToFirstWrapper">
												<div className="scrollToFirst" onClick={scrollToFirst}>
													{_("championship.leaderboard.scrollToFirst")}
												</div>
											</div>
										)}

										{!meInView &&
										leaderboards.length > 15 &&
										championship?.optedIn === true &&
										championship?.matchesPlayed > 0 &&
										!hasExpiredTrial() ? (
											<div className="scrollToMeWrapper">
												<div className="scrollToMe" onClick={scrollToMe}>
													{_("championship.leaderboard.scrollToMe")}
												</div>
											</div>
										) : null}

										{leaderboards[0] &&
										(leaderboards[0].rank.rank > 1 || !leaderboards[0].rank.rank) ? (
											<InvisibleInfiniteLoader
												onViewChange={onViewChangeUp}
												rootMargin={"-200px 0px 0px 0px"}
												loading={loading}
											/>
										) : null}

										{leaderboards.map(renderRow)}

										{hasMore ? (
											<InvisibleInfiniteLoader
												onViewChange={onViewChangeDown}
												loading={loading}
											/>
										) : null}
									</div>
								</div>
							</div>
							<div className="column columnRight">
								<AdWidget location={AdLocation.ChampionshipSidebar} className="aBlock" />
								{championship?.optedIn === true && (
									<UpcomingReservations
										params={{ championshipId: championship.uuid }}
										label={_("championship.upcomingMatches.heading")}
									/>
								)}
								<Challenges
									challenges={championship?.challenges}
									activeOrExpiredTrial={hasActiveOrExpiredTrial()}
									onChallengeSelected={onChallengeSelected}
								/>
								{championship?.optedIn === true && isTournament === false && <ChampionshipAbout />}
							</div>
						</>
					)}
				</div>
			</div>
		</ChampionshipContext.Provider>
	);
};

export default withRedux(
	{
		loadLeaderboardFirstPage,
		reloadMyChampionships,
		showAddToChampionshipWatchListModal,
		getChampionshipSuggestions,
		showComparisonLeaderboardModal,
		showChallengeLeaderboardModal,
		showInviteModal,
	},
	({ championships: { championships, firstPages } }: RootState, { uuid }: Props) => {
		let firstPage = [];

		if (firstPages[uuid]) {
			firstPage = firstPages[uuid];
		}

		const championship = championshipSelector(championships, uuid);

		return { championship, firstPage };
	}
)(Leaderboard);
